import { DEFAULT_OG_IMAGE, SITE_URL } from "./seoConfig"

const absoluteUrl = (pathOrUrl?: string) => {
  if (!pathOrUrl) return `${SITE_URL}${DEFAULT_OG_IMAGE}`

  return pathOrUrl.startsWith("http") ? pathOrUrl : `${SITE_URL}${pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`}`
}

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "catalojei",
  url: SITE_URL,
  logo: absoluteUrl(DEFAULT_OG_IMAGE),
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: "contato@catalojei.com.br",
    areaServed: "BR",
    availableLanguage: ["Portuguese"],
  },
}

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "catalojei",
  url: SITE_URL,
  inLanguage: "pt-BR",
  description:
    "Plataforma para criar loja virtual, cadastrar produtos, receber pedidos e vender pela internet.",
}

export const softwareSchema = {
  "@context": "https://schema.org",
  "@type": ["SoftwareApplication", "WebApplication"],
  name: "catalojei",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  offers: { "@type": "Offer", price: "0", priceCurrency: "BRL" },
  description:
    "Sistema de loja virtual para criar loja online, cadastrar produtos, receber pedidos, aceitar pagamento online, emitir cupom fiscal (NFC-e) e vender pela internet.",
}

export const productSchema = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "catalojei - Plataforma de loja virtual",
  brand: "catalojei",
  description:
    "Plataforma de e-commerce para pequenos negócios criarem loja virtual, divulgarem produtos, emitirem cupom fiscal (NFC-e) e venderem online.",
}

export const homeBreadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Início",
      item: SITE_URL,
    },
  ],
}

export const supportFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Como criar uma loja virtual?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Você pode criar uma conta, cadastrar produtos, organizar categorias, configurar formas de pagamento e divulgar o link da sua loja online para começar a receber pedidos.",
      },
    },
    {
      "@type": "Question",
      name: "Posso criar uma loja online grátis?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Sim. O catalojei possui plano gratuito para pequenos negócios iniciarem uma loja virtual e evoluírem conforme a operação crescer.",
      },
    },
    {
      "@type": "Question",
      name: "Preciso ter CNPJ para vender online?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Você pode começar a organizar sua loja online mesmo como empreendedor em fase inicial, respeitando as regras fiscais aplicáveis ao seu tipo de negócio.",
      },
    },
    {
      "@type": "Question",
      name: "Que tipo de produto posso vender na loja virtual?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "A plataforma atende roupas, cosméticos, acessórios, eletrônicos, artesanato, comida, presentes, produtos digitais e outros segmentos permitidos.",
      },
    },
    {
      "@type": "Question",
      name: "Consigo vender pelo Instagram e WhatsApp usando o link da loja?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Sim. Você pode compartilhar o link da loja nas redes sociais e no WhatsApp para apresentar produtos e receber pedidos online.",
      },
    },
    {
      "@type": "Question",
      name: "A loja aceita pagamento online?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "A loja pode trabalhar com pagamento online via integração disponível e também com formas próprias configuradas conforme a operação.",
      },
    },
    {
      "@type": "Question",
      name: "O catalojei emite cupom fiscal (NFC-e)?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Sim. A loja pode configurar a integração fiscal e emitir NFC-e diretamente no fechamento do pedido, além de importar e organizar as notas fiscais de compra recebidas dos fornecedores.",
      },
    },
  ],
}

export const buildBreadcrumbSchema = (items: Array<{ name: string; path: string }>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
})

export const buildStoreSchema = (name: string, path: string, description?: string, image?: string) => [
  {
    "@context": "https://schema.org",
    "@type": "Store",
    name,
    description: description || `Compre online na loja ${name}. Veja produtos, preços e faça seu pedido pela internet.`,
    url: `${SITE_URL}${path}`,
    image: absoluteUrl(image),
  },
  buildBreadcrumbSchema([
    { name: "Início", path: "/" },
    { name, path },
  ]),
]

export const buildProductSchema = ({
  name,
  storeName,
  path,
  description,
  image,
  sku,
  price,
  available,
}: {
  name: string
  storeName: string
  path: string
  description?: string
  image?: string
  sku?: string
  price?: number
  available?: boolean
}) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name,
  description: description || `Compre ${name} na loja ${storeName}. Veja preço, descrição e faça seu pedido online.`,
  image: absoluteUrl(image),
  sku,
  brand: {
    "@type": "Brand",
    name: storeName,
  },
  offers:
    typeof price === "number"
      ? {
          "@type": "Offer",
          url: `${SITE_URL}${path}`,
          priceCurrency: "BRL",
          price,
          availability: available === false ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
        }
      : undefined,
})
